import { apiFetch } from '../utils/apiClient';
import type { Task } from '../types';

/**
 * Two-phase upload flow (2026-04-24). The browser hits `/presign` to get a
 * short-lived pre-signed PUT URL + attachment id, uploads bytes directly to
 * S3, then calls commit so the backend verifies the upload landed and
 * records it on either task.attachments or task.notes[noteId].attachments.
 */

export interface PresignResponse {
  attachmentId: string;
  s3Key: string;
  uploadUrl: string;
}

export async function presignAttachment(
  teamId: string,
  taskId: string,
  file: File
): Promise<PresignResponse> {
  return apiFetch<PresignResponse>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/attachments/presign`,
    {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      }),
    }
  );
}

export async function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  // Using XHR instead of fetch so we can surface progress; fetch has no
  // standard upload-progress API yet.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream'
    );
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
    xhr.send(file);
  });
}

export async function commitAttachment(
  teamId: string,
  taskId: string,
  input: {
    attachmentId: string;
    filename: string;
    contentType: string;
    size: number;
    noteId?: string;
  }
): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/attachments`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  );
  return task;
}

export async function deleteAttachment(
  teamId: string,
  taskId: string,
  attachmentId: string
): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE' }
  );
  return task;
}

/**
 * Full upload flow as a single call: presign → PUT → commit. Returns the
 * updated task. Throws on any phase failure.
 */
export async function uploadAttachment(
  teamId: string,
  taskId: string,
  file: File,
  opts?: { noteId?: string; onProgress?: (percent: number) => void }
): Promise<Task> {
  const presigned = await presignAttachment(teamId, taskId, file);
  await uploadToS3(presigned.uploadUrl, file, opts?.onProgress);
  return commitAttachment(teamId, taskId, {
    attachmentId: presigned.attachmentId,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    noteId: opts?.noteId,
  });
}
